import { ReactElement, ReactNode } from 'react';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import { NextIntlClientProvider } from 'next-intl';
import ErrorBoundary from '@/components/ErrorBoundary';
import DetailPageSkeleton from '@/components/SkeletonDetailPage/mIndex';
import DetailPageSkeletonPc from '@/components/SkeletonDetailPage';
import { isMobile } from '@/utils';
import { useSls, useSkeletonLoading, useLoginInfo, usePermission } from '@/hooks';
import UseContextProvider from '@/context';
import RootLayout from '../components/Layout/RootLayout';
import ZhHans from '../../locales/zh-Hans.json';
import '@/styles/app.css';
import '@/styles/mobile.rc.scss';
import '@/styles/pc.common.css';
import '@/styles/app_store_webview.css';
import { useRouter } from 'next/router';
import ResponsiveLayout from '@/components/ResponsiveLayout';
import { SafeAreaCtx, usePageInitInAppStore } from '@/utils/pageEntry';
import { ClickToComponent } from 'click-to-react-component';
import TestTools from '@/components/TestTools';

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement, pageProps: any) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export default function App({ Component, pageProps }: AppPropsWithLayout) {

  const { loading, previousUrl } = useSkeletonLoading();
  const account = useLoginInfo();
  const { pathname } = useRouter();
  const permission = usePermission();

  useSls();

  const getLayout = Component.getLayout ?? ((page) => page);

  const { pageInitLoading, safeAreaHeight } = usePageInitInAppStore();

  if (pageInitLoading) {
    return null;
  }

  // 主渲染函数, 暂时过渡使用
  const renderContent = () => {
    if (loading) {
        return isMobile ? '' : <DetailPageSkeletonPc />;
    }
    // 还没修改详情页布局，暂时走该判断逻辑
    if (isMobile || ['/detail', '/edit'].includes(pathname)) {
      return getLayout(
        <ErrorBoundary isMobile={isMobile}>
          <Component {...pageProps} account={account} previousUrl={previousUrl} />
        </ErrorBoundary>,
        pageProps
      );
    }
    return (
      <ErrorBoundary isMobile={isMobile}>
        <ResponsiveLayout
          pageProps={pageProps}
          Component={Component}
        />
       </ErrorBoundary>
    );
  };

  
  return (
    <>
      {['dev'].includes(process.env.ENV || '') && (
        <ClickToComponent />
      )}
      <Head>
        <meta name="viewport" content="user-scalable=no" />
        <meta name='viewport' content='width=device-width, initial-scale=1, user-scalable=no' />
      </Head>
      <Script id='jweixin-sdk' strategy='afterInteractive'>
        {`!function(){var e=window.navigator.userAgent;if(e&&/(micromessenger|miniprogram)/i.test(e.toLowerCase())){var t=document.createElement("script"),n=document.getElementsByTagName("script")[0];t.async=1,t.src="//res.wx.qq.com/open/js/jweixin-1.6.0.js",n.parentNode.insertBefore(t,n)}}()`}
      </Script>
      <UseContextProvider>
        <NextIntlClientProvider
          locale='zh'
          messages={ZhHans}
          onError={() => null}
        >
          <SafeAreaCtx.Provider value={{ safeAreaHeight }}>
            <RootLayout account={account} permission={permission}>
              {/* <TestTools /> */}
              {renderContent()}
            </RootLayout>
          </SafeAreaCtx.Provider>
        </NextIntlClientProvider>
      </UseContextProvider>
    </>
  );
}
